/**
 * Avatars Tab — Superadmin-managed catalog of HeyGen talking-head avatars.
 * Distinct from User.avatar_url profile pictures.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminListAvatars,
  adminCreateAvatar,
  adminUpdateAvatar,
  adminSetDefaultAvatar,
  adminDeleteAvatar,
} from '@/services/avatarCatalogApi';
import type {
  Avatar,
  AvatarCreateRequest,
  AvatarUpdateRequest,
} from '@/types/avatar';

interface FormState {
  name: string;
  heygen_id: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

const emptyForm: FormState = {
  name: '',
  heygen_id: '',
  description: '',
  sort_order: 0,
  is_active: true,
};

export default function AvatarsTab() {
  const { t } = useTranslation('admin');
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadAvatars();
  }, []);

  const loadAvatars = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await adminListAvatars();
      setAvatars(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load avatars');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (a: Avatar) => {
    setEditingId(a.id);
    setForm({
      name: a.name,
      heygen_id: a.heygen_id,
      description: a.description ?? '',
      sort_order: a.sort_order,
      is_active: a.is_active,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.heygen_id.trim()) {
      alert('Name and HeyGen ID are required');
      return;
    }
    setBusy(true);
    try {
      if (editingId === null) {
        const payload: AvatarCreateRequest = {
          name: form.name.trim(),
          heygen_id: form.heygen_id.trim(),
          description: form.description.trim() || null,
          sort_order: form.sort_order,
        };
        await adminCreateAvatar(payload);
      } else {
        const payload: AvatarUpdateRequest = {
          name: form.name.trim(),
          heygen_id: form.heygen_id.trim(),
          description: form.description.trim() || null,
          sort_order: form.sort_order,
          is_active: form.is_active,
        };
        await adminUpdateAvatar(editingId, payload);
      }
      closeForm();
      await loadAvatars();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save avatar');
    } finally {
      setBusy(false);
    }
  };

  const handleSetDefault = async (id: number) => {
    setBusy(true);
    try {
      await adminSetDefaultAvatar(id);
      await loadAvatars();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to set default');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (a: Avatar) => {
    if (a.is_default) {
      alert('Cannot delete the default avatar; set a different default first.');
      return;
    }
    if (!confirm(`Soft-delete avatar "${a.name}"?`)) return;
    setBusy(true);
    try {
      await adminDeleteAvatar(a.id);
      await loadAvatars();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete avatar');
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          {t('avatars.title', { defaultValue: 'Avatars' })}
        </h2>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={busy}
        >
          {t('avatars.addAvatar', { defaultValue: 'Add avatar' })}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-4">
          {error}
          <button onClick={loadAvatars} className="ml-4 underline">
            {t('common.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">HeyGen ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sort</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {avatars.map((a) => (
              <tr key={a.id} className={a.is_active ? '' : 'opacity-50'}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700 font-mono text-xs">{a.heygen_id}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{a.description ?? '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{a.sort_order}</td>
                <td className="px-4 py-3 text-sm">
                  {a.is_active ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">yes</span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">no</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  {a.is_default ? (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">default</span>
                  ) : (
                    <button
                      onClick={() => handleSetDefault(a.id)}
                      disabled={busy || !a.is_active}
                      className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                    >
                      set default
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-right space-x-3">
                  <button
                    onClick={() => openEdit(a)}
                    disabled={busy}
                    className="text-blue-600 hover:underline disabled:opacity-50"
                  >
                    edit
                  </button>
                  <button
                    onClick={() => handleDelete(a)}
                    disabled={busy || a.is_default}
                    className="text-red-600 hover:underline disabled:opacity-50"
                  >
                    delete
                  </button>
                </td>
              </tr>
            ))}
            {avatars.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                  No avatars in catalog.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingId === null ? 'Create avatar' : 'Edit avatar'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HeyGen ID (UUID)</label>
                <input
                  type="text"
                  value={form.heygen_id}
                  onChange={(e) => setForm({ ...form, heygen_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded font-mono text-xs"
                  placeholder="65cca4cf-b7c8-4619-871f-84e2cf8b21d4"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })
                  }
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              {editingId !== null && (
                <div className="flex items-center">
                  <input
                    id="is_active"
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    Active (visible to users)
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={closeForm}
                disabled={busy}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={busy}
                className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
